import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';
import AboutContent from './AboutContent';
import AboutContentLeft from './AboutContentLeft';
import AboutContentRight from './AboutContentRight';
import PageSEO from './PageSEO'; // Import the SEO component

const AboutView = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Array of images for the photo widget with enhanced alt text
    const images = [
        "/images/about/john-kinder-1946.jpg",
        "/images/about/kinders-great-service.webp", 
        "/images/about/john-kinder-1982.webp",
        "/images/about/john-kinder-1986.webp",
        "/images/about/kinders-customer-first-always.webp",
        "/images/about/kinders-catering.webp",
        "/images/about/kinders-sauces.webp"
    ];

    // Comprehensive SEO configuration for about page
    const aboutSEO = {
        title: "About Kinder's Meats | Three Generations of BBQ Excellence Since 1946",
        description: "Discover the story of Kinder's Meats Deli BBQ & Catering - a family-owned business serving authentic BBQ across 7 California locations since 1946. From WWII veteran John Kinder to third-generation CEO Justin Kinder, learn about our commitment to quality, community, and award-winning BBQ traditions.",
        keywords: [
            "Kinder's Meats history",
            "family-owned BBQ restaurant",
            "John Kinder founder",
            "Dan Kinder",
            "Justin Kinder CEO",
            "BBQ restaurant since 1946",
            "California BBQ heritage",
            "East Bay BBQ history",
            "three generations BBQ",
            "family business California",
            "WWII veteran restaurant owner",
            "BBQ tradition",
            "award-winning BBQ sauces",
            "Ball Tip Sandwich",
            "Richmond California restaurant",
            "Concord BBQ restaurant history",
            "family legacy restaurant",
            "artisan BBQ sauces",
            "traditional BBQ methods",
            "community-focused restaurant"
        ],
        canonicalUrl: "/about",
        ogImage: "https://kindersmeats.com/images/about/john-kinder-1946.webp",
        customMeta: {
            'article:author': 'Kinder Family',
            'article:section': 'Company History',
            'business:founded': '1946',
            'business:founders': 'John Kinder'
        },
        schema: {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "AboutPage",
                    "@id": "https://kindersmeats.com/about#aboutpage",
                    "url": "https://kindersmeats.com/about",
                    "name": "About Kinder's Meats | Three Generations of BBQ Excellence Since 1946",
                    "description": "The story of Kinder's Meats - from WWII veteran John Kinder's 1946 vision to today's third-generation leadership under CEO Justin Kinder.",
                    "mainEntity": {
                        "@id": "https://kindersmeats.com/#organization"
                    },
                    "about": {
                        "@id": "https://kindersmeats.com/#organization" 
                    }
                },
                {
                    "@type": "Organization", 
                    "@id": "https://kindersmeats.com/#organization",
                    "name": "Kinder's Meats Deli BBQ & Catering",
                    "alternateName": "Kinder's Meats",
                    "url": "https://kindersmeats.com",
                    "logo": {
                        "@type": "ImageObject",
                        "url": "https://kindersmeats.com/images/kinders-logo.png",
                        "width": 300,
                        "height": 100
                    },
                    "image": [
                        {
                            "@type": "ImageObject",
                            "url": "https://kindersmeats.com/images/about/john-kinder-1946.webp",
                            "caption": "John Kinder, founder of Kinder's Meats in 1946",
                            "width": 800,
                            "height": 600
                        },
                        {
                            "@type": "ImageObject", 
                            "url": "https://kindersmeats.com/images/about/kinders-sauces.webp",
                            "caption": "Kinder's award-winning BBQ sauces and spices",
                            "width": 800,
                            "height": 600
                        }
                    ],
                    "description": "Family-owned BBQ restaurant and catering company serving authentic barbecue across 7 California locations since 1946. Founded by WWII veteran John Kinder, now led by third-generation CEO Justin Kinder.",
                    "foundingDate": "1946",
                    "foundingLocation": {
                        "@type": "Place",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "13th Street and Lincoln Avenue",
                            "addressLocality": "Richmond",
                            "addressRegion": "CA",
                            "addressCountry": "US"
                        }
                    },
                    "founder": {
                        "@type": "Person",
                        "name": "John Kinder",
                        "birthDate": "1922-01-10",
                        "deathDate": "2012-05-13",
                        "description": "WWII veteran who founded Kinder's Meats in 1946 with a mission to serve the finest meats and make every guest feel like family.",
                        "knowsAbout": ["Butchery", "Customer Service", "Community Building"]
                    },
                    "employees": [
                        {
                            "@type": "Person",
                            "name": "Dan Kinder", 
                            "jobTitle": "Former Owner/Operator",
                            "description": "Second-generation owner who took over operations in 1982 and perfected the famous Ball Tip Sandwich."
                        },
                        {
                            "@type": "Person",
                            "name": "Paula Kinder",
                            "jobTitle": "Former Co-Owner",
                            "description": "Co-owner known for community involvement and helping establish Kinder's as a local landmark."
                        },
                        {
                            "@type": "Person",
                            "name": "Justin Kinder",
                            "jobTitle": "CEO and President", 
                            "description": "Third-generation leader carrying forward the family legacy with tradition and innovation."
                        }
                    ],
                    "award": [
                        "Award-winning BBQ sauces and spices",
                        "East Bay's favorite BBQ restaurant"
                    ],
                    "knowsAbout": [
                        "Barbecue",
                        "Butchery", 
                        "Catering",
                        "Sauce making",
                        "Family business traditions",
                        "Community service"
                    ],
                    "hasOfferCatalog": {
                        "@type": "OfferCatalog",
                        "name": "Kinder's Signature Products",
                        "itemListElement": [
                            {
                                "@type": "Offer",
                                "itemOffered": {
                                    "@type": "MenuItem",
                                    "name": "Ball Tip Sandwich",
                                    "description": "Kinder's most famous sandwich, perfected in the 1980s"
                                }
                            },
                            {
                                "@type": "Offer",
                                "itemOffered": {
                                    "@type": "Product",
                                    "name": "BBQ Sauces & Marinades",
                                    "description": "Award-winning sauces from Mild to EXXXtra Hot, shipped nationwide"
                                }
                            },
                            {
                                "@type": "Offer", 
                                "itemOffered": {
                                    "@type": "MenuItem",
                                    "name": "Cut-to-Order Premium Meats",
                                    "description": "Hand-cut premium steaks and marinated meats"
                                }
                            }
                        ]
                    },
                    "address": {
                        "@type": "PostalAddress",
                        "addressRegion": "CA",
                        "addressCountry": "US"
                    },
                    "areaServed": [
                        {
                            "@type": "City",
                            "name": "Brentwood",
                            "containedInPlace": { "@type": "State", "name": "California" }
                        },
                        {
                            "@type": "City", 
                            "name": "Concord",
                            "containedInPlace": { "@type": "State", "name": "California" }
                        },
                        {
                            "@type": "City",
                            "name": "Hercules", 
                            "containedInPlace": { "@type": "State", "name": "California" }
                        },
                        {
                            "@type": "City",
                            "name": "Martinez",
                            "containedInPlace": { "@type": "State", "name": "California" }
                        },
                        {
                            "@type": "City",
                            "name": "Pleasant Hill",
                            "containedInPlace": { "@type": "State", "name": "California" }
                        },
                        {
                            "@type": "City",
                            "name": "Walnut Creek", 
                            "containedInPlace": { "@type": "State", "name": "California" }
                        }
                    ],
                    "slogan": "Serving neighbors the finest meats and making every guest feel like family",
                    "brand": {
                        "@type": "Brand",
                        "name": "Kinder's",
                        "description": "Authentic California BBQ and premium meats since 1946"
                    }
                },
                {
                    "@type": "WebPage",
                    "@id": "https://kindersmeats.com/about#webpage",
                    "url": "https://kindersmeats.com/about", 
                    "name": "About Kinder's Meats | Three Generations of BBQ Excellence Since 1946",
                    "description": "Discover the story of Kinder's Meats - from WWII veteran John Kinder's 1946 vision to today's third-generation leadership under CEO Justin Kinder.",
                    "isPartOf": {
                        "@id": "https://kindersmeats.com/#website"
                    },
                    "about": {
                        "@id": "https://kindersmeats.com/#organization"
                    },
                    "mainEntity": {
                        "@id": "https://kindersmeats.com/#organization"
                    },
                    "breadcrumb": {
                        "@id": "https://kindersmeats.com/about#breadcrumb"
                    }
                },
                {
                    "@type": "BreadcrumbList",
                    "@id": "https://kindersmeats.com/about#breadcrumb",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home", 
                            "item": "https://kindersmeats.com"
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "About Us",
                            "item": "https://kindersmeats.com/about"
                        }
                    ]
                }
            ]
        }
    };

    return (
        <>
            {/* SEO Component */}
            <PageSEO {...aboutSEO} />
            
            {/* Main content with semantic HTML structure */}
            <main>
                <section className="theme-dark section m-0 p-0" data-theme="dark">
                    <div className='is-flex is-flex-direction-column is-justify-content-start'>
                        <Navigation 
                          colorMode="colorBlack"
                        />
                        
                        {/* Hero Section */}
                        <header itemScope itemType="https://schema.org/AboutPage">
                            <AboutContent 
                                className="content-space-160-desktop is-10-mobile is-10-tablet is-8-desktop is-10-widescreen is-10-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-2-desktop is-offset-1-widescreen is-offset-1-fullhd"
                                title="About Kinder's"
                                image={images[2]}
                                imageAlt="John Kinder in 1982 - Second generation leadership transitioning the family business"
                                body={
                                    <p className='colorWhite'>
                                        Serving seven East Bay locations with the perfect blend of service, flavor, and speed, Kinder's is proud to operate family-owned locations in <Link to="/locations/brentwood" className="text-link">Brentwood</Link>, <Link to="/locations/concord" className="text-link">Concord</Link>, <Link to="/locations/hercules" className="text-link">Hercules</Link>, <Link to="/locations/martinez" className="text-link">Martinez</Link>, <Link to="/locations/pleasant-hill" className="text-link">Pleasant Hill</Link>, and <Link to="/locations/walnut-creek-ygnacio" className="text-link">Walnut Creek</Link>. As an active part of each community, we deliver hand-cut premium meats, made-to-order sandwiches, signature slow-smoked ribs and Marinated Ball Tip, plus our award-winning sauces and spices, nationwide. 
                                        <Link to="/locations" className="text-link"> Visit our locations</Link> to experience the difference.
                                    </p>
                                }
                                colorMode="colorWhite"
                                classSpacer="content-space-200"
                            />
                        </header>

                        {/* Historical Timeline */}
                        <article itemScope itemType="https://schema.org/Organization">
                            {/* 1946 - Founding */}
                            <section aria-labelledby="founding-1946">
                                <AboutContentRight 
                                    className="content-space-160-desktop is-10-mobile is-10-tablet is-8-desktop is-10-widescreen is-10-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-2-desktop is-offset-1-widescreen is-offset-1-fullhd"
                                    title="1946"
                                    subtitle="A Family Dream Begins"
                                    body="John Kinder (Jan 10, 1922 – May 13, 2012) returned from WWII with a simple mission: serve his neighbors the finest meats and make every guest feel like family. In a humble storefront on the corner of 13th Street and Lincoln Avenue in Richmond, California, he built Kinder's reputation on respect for the craft, for the customer, and the community."
                                    image={images[0]}
                                    imageAlt="John Kinder in 1946 - WWII veteran and founder of Kinder's Meats starting the family business in Richmond, California"
                                    colorMode="colorWhite"
                                    classSpacer="content-space-100"
                                />
                            </section>

                            {/* 1980s - Second Generation */}
                            <section aria-labelledby="growth-1980s">
                                <AboutContentLeft 
                                    className="content-space-160-desktop is-10-mobile is-10-tablet is-8-desktop is-10-widescreen is-10-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-2-desktop is-offset-1-widescreen is-offset-1-fullhd"
                                    title="The '80s"
                                    subtitle="Growth with Heart"
                                    body="In 1982 Dan Kinder and his then wife, Paula Kinder, took the reins, relocating to Concord in 1985. Dan's knack for operations and Paula's passion for community involvement transformed the shop into a local landmark. It was here that they perfected the Kinder's Ball Tip Sandwich, which remains our most famous sandwich today, and laid the groundwork for Kinder's future."
                                    image={images[1]}
                                    imageAlt="Kinder's Meats great service in the 1980s - Dan and Paula Kinder transforming the business into a local landmark"
                                    colorMode="colorWhite"
                                    classSpacer="content-space-100"
                                />
                            </section>

                            {/* Sauces & Spices */}
                            <section aria-labelledby="sauces-expansion">
                                <AboutContentRight 
                                    className="content-space-160-desktop is-10-mobile is-10-tablet is-8-desktop is-10-widescreen is-10-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-2-desktop is-offset-1-widescreen is-offset-1-fullhd"
                                    title="The Next Chapter"
                                    subtitle="Sauces & Spices"
                                    body="Kinder's began bottling its proprietary sauces, marinades, and rubs in the late 1980s. Each recipe was rigorously tested for balance, using only the highest-grade herbs, spices, and natural ingredients. Favorites range from Mild to 'EXXXtra Hot,' and fans love mixing blends to their taste. These award-winning flavors are now stocked on grocery shelves and shipped nationwide, bringing Kinder's quality and creativity into thousands more kitchens."
                                    image={images[6]}
                                    imageAlt="Kinder's award-winning BBQ sauces and spices - Proprietary blends from Mild to EXXXtra Hot shipped nationwide"
                                    colorMode="colorWhite"
                                    classSpacer="content-space-100"
                                />
                            </section>

                            {/* Third Generation */}
                            <section aria-labelledby="third-generation">
                                <AboutContentLeft 
                                    className="content-space-160-desktop is-10-mobile is-10-tablet is-8-desktop is-10-widescreen is-10-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-2-desktop is-offset-1-widescreen is-offset-1-fullhd"
                                    title="Today"
                                    subtitle="Third-Generation Leadership"
                                    body="Justin Kinder takes over as CEO and President, carrying forward the family legacy every day. He blends tradition with fresh ideas as he oversees product development, quality control, and our seamless digital ordering experience. Under his guidance, Kinder's remains obsessed with quality: from our cut-to-order steaks and marinated meats by the pound to made-to-order deli and slow-smoked BBQ specialties."
                                    image={images[5]}
                                    imageAlt="Modern Kinder's catering setup - Third-generation CEO Justin Kinder leading with tradition and innovation"
                                    colorMode="colorWhite"
                                    classSpacer="content-space-100"
                                />
                            </section>
                        </article>

                        <div className='m-6'>
                            <p></p>
                        </div>
                        
                        <Footer 
                          colorMode="colorBlack"
                        />
                    </div>
                </section>
            </main>
        </>
    );
};

export default AboutView;